import {
  Box,
  Typography,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  SelectChangeEvent,
  CircularProgress,
} from "@mui/material";
import { Convertor, Footer, Header, BSBCoin } from "components/shared";
import { useEffect, useState } from "react";
import { fetchPublic, SetFlag } from "utils";
import { useUtility } from "utils/context/UtilityContext";
import { PriceInfo, PricePackage } from "utils/types/PricingTypes";

// Define the country data array
const countryData = [
  {
    currencyCode: "CDF",
    currencySymbol: "Fc",
    countryCode: "CD",
    countryName: "République Démocratique du Congo",
    currencyName: "CDF",
  },
  {
    currencyCode: "XAF",
    currencySymbol: "CFA",
    countryCode: "CG",
    countryName: "République du Congo",
    currencyName: "XAF",
  },
  // Add more countries here as needed
];

const localStorageCountryIndexKey = "selectedCountryIndex";

export const Prices = () => {
  const { marginBottomHeader } = useUtility();
  const [priceInfo, setPriceInfo] = useState<PriceInfo | null>(null);
  const [images, setImages] = useState<Record<string, string> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // **MODIFIED**: Initialize state from localStorage
  const [selectedCountryIndex, setSelectedCountryIndex] = useState(() => {
    const savedIndex = localStorage.getItem(localStorageCountryIndexKey);
    const index = savedIndex ? parseInt(savedIndex, 10) : 0;
    // Fallback to 0 if the saved index is out of bounds
    return countryData[index] ? index : 0;
  });

  // **MODIFIED**: Save selected country to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem(localStorageCountryIndexKey, selectedCountryIndex.toString());

    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        const selectedCountryData = countryData[selectedCountryIndex];

        const [priceResponse, imageResponse] = await Promise.all([
          fetchPublic(`/public/price/USD/${selectedCountryData.currencyCode}`),
          fetchPublic(`/public/images/packages`),
        ]);

        if (priceResponse.status === "success" && priceResponse.data) {
          setPriceInfo({
            rate: priceResponse.data.rate,
            basePrices: priceResponse.data.basePrices,
            usd: priceResponse.data.usd,
            target: priceResponse.data.target,
          });
        } else {
          throw new Error(priceResponse.message || "Failed to fetch prices.");
        }

        if (imageResponse) {
          const imageMap: Record<string, string> = {};
          imageResponse.forEach((img: { id: string; url: string }) => {
            const packageName = img.id.replace("_png", "");
            imageMap[packageName] = img.url;
          });
          setImages(imageMap); 
        } else {
          throw new Error("Failed to fetch images.");
        }
      } catch (err: any) {
        setError(err.message || "An error occurred while loading data.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedCountryIndex]);

  const handleCountryChange = (event: SelectChangeEvent<number>) => {
    setSelectedCountryIndex(event.target.value as number);
  };

  const packageKeys: (keyof PricePackage)[] = ["essential", "advanced", "premium", "elite"];
  const packageLabels: Record<string, string> = {
    essential: "Essentiel",
    advanced: "Avancé",
    premium: "Premium",
    elite: "Élite",
  };

  const borderColors: Record<string, string> = {
    essential: "#1e88e5",
    advanced: "#43a047",
    premium: "#fb8c00",
    elite: "#d32f2f",
  };

  const isDataReady = !loading && priceInfo && priceInfo.usd && priceInfo.target && priceInfo.basePrices && images;

  return (
    <Box className={`${marginBottomHeader} flex flex-col items-center gap-5`}>
      <Header />
      <Box
        sx={{
          width: "100%",
          maxWidth: "1200px",
          textAlign: "center",
          px: { xs: 2, sm: 4 },
          py: { xs: 4, sm: 6 },
        }}
      >
        <Typography variant="h3" component="h1" sx={{ mb: 2, fontWeight: 'bold' }}>
          Grille tarifaire des BSB COINS
        </Typography>

        <FormControl sx={{ minWidth: 240, mb: 6 }}>
          <InputLabel id="country-select-label">Sélectionnez un pays</InputLabel>
          <Select
            labelId="country-select-label"
            value={selectedCountryIndex}
            onChange={handleCountryChange}
            label="Sélectionnez un pays"
          >
            {countryData.map((country, index) => (
              <MenuItem key={country.currencyCode} value={index}>
                <Box display="flex" alignItems="center" gap={1.5}>
                  <SetFlag
                    countryCode={country.countryCode}
                    countryName={country.countryName}
                  />
                  <Typography variant="body1">
                    {country.countryName} ({country.currencyName})
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {!isDataReady ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 10 }}>
            {loading ? <CircularProgress /> : <Typography color="error">{error || "Could not load pricing data."}</Typography>}
          </Box>
        ) : (
          <>
            <Grid container spacing={{ xs: 2, md: 4 }} justifyContent="center" sx={{ mb: 8 }}>
              {packageKeys.map((key) => (
                <Grid item xs={12} sm={6} md={3} key={key}>
                  <Box
                    sx={{
                      border: `2px solid ${borderColors[key]}`,
                      borderRadius: "16px",
                      p: 3,
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      backgroundColor: "#f8f9fa",
                      transition: "transform 0.3s ease, box-shadow 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-8px)",
                        boxShadow: "0 10px 20px rgba(0,0,0,0.1)",
                      }
                    }}
                  >
                    <Box>
                      <Typography variant="h5" sx={{ fontWeight: "bold", mb: 1 }}>
                        {packageLabels[key]}
                      </Typography>
                      <Typography variant="h5" sx={{ color: borderColors[key], mb: 1 }}>
                        {priceInfo.usd[key]} $
                      </Typography>
                      <Typography variant="h6" sx={{ color: "#5f6c7b", mb: 2 }}>
                        {priceInfo.target[key]}{" "}
                        {countryData[selectedCountryIndex].currencySymbol}
                      </Typography>
                      <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>
                        {priceInfo.basePrices[key]} BSB COINS
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        display: "flex",
                        height: 150,
                        justifyContent: "center",
                        alignItems: "center",
                        mt: 2,
                      }}
                    >
                      {images[key] ? (
                        <Tooltip title={`BSBCoin ${packageLabels[key]}`}>
                          <img
                            src={images[key]}
                            alt={packageLabels[key]}
                            style={{ maxHeight: "100%", maxWidth: "100%" }}
                          />
                        </Tooltip>
                      ) : (
                        <BSBCoin />
                      )}
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
            
            {/* The Convertor is only rendered when data is ready, providing a safeguard */}
            <Box sx={{ maxWidth: 800, mx: 'auto', textAlign: 'left' }}>
               <Typography variant="h4" component="h2" sx={{ mb: 3, fontWeight: 'bold' }}>
                Convertisseur de devises
               </Typography>
               <Typography variant="body1" sx={{ mb: 1 }}>
                 Pour les BSB Coins corespondant a 10,25,50 ou 100 qui sont
                 respectivement les packets Essentiel, Avancé, Premium et Elite
                 referevez vous a la grille tarifaire si dessus
               </Typography>
              <Convertor selectedCountry={countryData[selectedCountryIndex]} />
              <Typography variant="body1" sx={{ mt: 3, textAlign: "justify" }}>
                Les frais présentés ici sont liés à notre service BSB (Brother, Same
                Business). Ils reflètent les coûts associés aux différentes options
                d'achat disponibles pour nos clients, afin de soutenir le bon
                fonctionnement de notre service tout en garantissant une expérience
                de haute qualité.
              </Typography>
            </Box>
          </>
        )}
      </Box>
      <Footer />
    </Box>
  );
};

